import type { ShopifyConnectionEntry } from '@common/api/authService';

/** Validate the authenticated response before exposing any connection action. */
export function parseShopifyConnectionEntry(value: unknown): ShopifyConnectionEntry {
    if (!value || typeof value !== 'object') throw new Error('Invalid Shopify connection entry');
    const entry = value as Record<string, unknown>;
    // Transitional backend contract before unified installation activation.
    if (entry.mode === 'manual' && entry.appName === 'Bustly' && entry.oauthAppId === undefined) {
        return {mode: 'manual', appName: 'Bustly'};
    }
    if (entry.mode !== 'shopify' || !['Bustly AI', 'Bustly'].includes(entry.appName as string) || typeof entry.launchUrl !== 'string') {
        throw new Error('Invalid Shopify connection entry');
    }
    const appName = entry.appName as 'Bustly' | 'Bustly AI';
    const oauthAppId = appName === 'Bustly AI' ? 'BACKUP' : 'PRIMARY';
    if (entry.oauthAppId !== undefined && entry.oauthAppId !== oauthAppId) throw new Error('Invalid Shopify application identity');
    // Exact Dev Dashboard installation selector; never allow arbitrary query redirects.
    if (entry.launchKind === 'development-install') {
        const installMatch = /^https:\/\/admin\.shopify\.com\/\?organization_id=[1-9][0-9]*&no_redirect=true&redirect=\/oauth\/redirect_from_developer_dashboard\?client_id%3[Dd][0-9a-f]{32}$/.exec(entry.launchUrl);
        if (!installMatch || installMatch[0] !== entry.launchUrl) {
            throw new Error('Invalid Shopify development install URL');
        }
        return { mode: 'shopify', appName, oauthAppId, launchUrl: entry.launchUrl, launchKind: 'development-install' };
    }
    if (entry.launchKind === 'admin') throw new Error('Shopify installation URL is required');
    // Validate the literal authority too: URL normalizes explicit default ports.
    const launchMatch = /^https:\/\/(admin\.shopify\.com|apps\.shopify\.com)(\/[^?#\s\\]*)?$/.exec(entry.launchUrl);
    if (!launchMatch) {
        throw new Error('Invalid Shopify launch URL');
    }
    const url = new URL(entry.launchUrl);
    const validKind = (entry.launchKind === 'admin' && url.hostname === 'admin.shopify.com') ||
        (entry.launchKind === 'app-store' && url.hostname === 'apps.shopify.com');
    if (!validKind) throw new Error('Invalid Shopify launch kind');
    // Decode the literal path before normalization, matching the backend URI boundary.
    // URL.pathname has already removed dot segments, including leading traversal.
    const decodedPath = decodeURIComponent(launchMatch[2] || '');
    if (entry.launchKind === 'app-store') {
        // A decoded leading // can become an authority when the backend reconstructs its URI.
        if (decodedPath.startsWith('//')) throw new Error('Invalid Shopify listing path');
        let depth = 0;
        for (const segment of decodedPath.split('/')) {
            if (!segment || segment === '.') continue;
            if (segment === '..') {
                if (depth === 0) throw new Error('Invalid Shopify listing path');
                depth -= 1;
            } else {
                depth += 1;
            }
        }
        if (depth === 0) throw new Error('Invalid Shopify listing path');
    }
    return { mode: 'shopify', appName, oauthAppId, launchUrl: entry.launchUrl, launchKind: entry.launchKind as 'admin' | 'app-store' };
}
